import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { useBuildAiQuizByFile } from '@/api/ai/useBuildAiQuiz';
import {
  ALLOWED_AI_FILE_EXTENSIONS,
  quizLevelToApiQuizDifficulty,
} from '@/shared/features/ai-quiz-generation/constants';
import { type BuildPromptOptions } from '@/shared/features/ai-quiz-generation/types';
import { alertDialog } from '@/ui_v3';

import {
  AI_GENERATION_EVENT_KEY,
  getPromptData,
  getStandardCodeData,
} from '../utils/getAiEventData';

import { type AchievementStandard, type UseAiByMethodParams } from './types';
import { useBuildAiFileErrorAlert } from './useBuildAiFileErrorAlert';

// 파일 최대 크기 검증 상수 (30MB)
const MAX_FILE_SIZE = 31457280; // 30MB in bytes

/**
 * File 방식 AI 퀴즈 생성 커스텀 훅
 *
 * 파일 업로드를 통해 퀴즈를 생성하는 File 생성 방식을 위한
 * UseAiByMethodReturn 인터페이스 구현체
 *
 * 주요 기능:
 * - 파일 선택기를 통한 파일 업로드
 * - 파일 타입 및 크기 검증 (PDF, DOCX, TXT, PPTX, CSV, HWP, 30MB)
 * - 기존 useBuildAiQuizByFile mutation 사용
 * - 콜백을 통해 성공/오류 처리를 상위 컴포넌트에 위임
 * - Query/YouTube 방식과의 일관성을 위한 공통 인터페이스 구현
 *
 * @param params - onGenerate, onError 콜백을 포함한 훅 파라미터
 * @returns UseAiByMethodReturn 인터페이스 구현
 */
export const useAiByFile = (params: UseAiByMethodParams) => {
  const { t } = useTranslation(['common', 'build']);
  const {
    onGenerateSuccess,
    onGenerateStart,
    onGenerateError,
    onAttach,
    onDetach,
    tryCount,
    generationId,
  } = params;

  // API 클라이언트의 기존 mutation 사용
  const { mutateAsync, isPending } = useBuildAiQuizByFile();
  const fileErrorAlert = useBuildAiFileErrorAlert();

  /**
   * 파일 첨부 처리
   * UI 컴포넌트로부터 파일을 전달받아 유효성을 검증한 후 첨부 상태를 업데이트
   *
   * UI 로직(파일 선택기, drag & drop)은 BuildAiModal이 담당하며,
   * 이 훅은 비즈니스 로직(검증, 상태 관리)만 처리
   *
   * @param attachment - UI 컴포넌트에서 선택/드롭된 첨부물 (File 방식은 File 타입만 처리)
   */
  const handleAttach = useCallback(
    async (attachment: File) => {
      // File 방식은 File 타입의 첨부물만 처리
      if (!(attachment instanceof File)) {
        return;
      }

      // 파일 타입 검증
      const isFileTypeValid = validateFileType(attachment);
      if (!isFileTypeValid) {
        alertDialog.open({
          header: {
            title: t('build:build.ai.notAllowedFileTitle'),
          },
          body: t('build:build.ai.notAllowedFileDescription', {
            fileExt: ALLOWED_AI_FILE_EXTENSIONS.join(', '),
          }),
        });
        return;
      }

      // 파일 크기 검증
      const isFileSizeValid = validateFileSize(attachment);
      if (!isFileSizeValid) {
        alertDialog.open({
          header: {
            title: t('build:build.ai.fileSizeExceed'),
          },
        });
        return;
      }

      // 검증 통과 시 첨부 파일 상태 업데이트
      onAttach(attachment);
    },
    [onAttach, t],
  );

  /**
   * 파일 첨부 해제 처리
   * 첨부된 파일을 제거하고 상태를 초기화
   */
  const handleDetach = useCallback(() => {
    onDetach();
  }, [onDetach]);

  /**
   * 파일로부터 퀴즈 생성 처리
   * 파일 존재 여부를 확인하고, API mutation을 호출하며, 적절한 콜백을 실행
   *
   * Note: Epic 5에서 prompt 파라미터 지원이 추가될 예정
   * 현재는 File API가 prompt를 받지 않으므로 사용되지 않음
   */
  const handleGenerate = useCallback(
    async (
      file: File,
      options: BuildPromptOptions,
      standards: AchievementStandard[],
      userPrompt?: string,
      // omit (undefined) 시 서버 기본 모델 결정. mutation factory 가
      // generationMode → generation_mode (snake_case, wire boundary) 로 변환.
      generationMode?: 'fast' | 'thorough',
      // Stage 1 UI cohort. wire 시 ui_cohort 로 변환. dropdown 일 때만
      // 서버가 Stage 2 backend_cohort (real/placebo) 를 평가한다.
      uiCohort?: 'as_is' | 'dropdown',
    ) => {
      // 파일 첨부 여부 확인
      onGenerateStart(tryCount > 0 ? true : false);
      const startTime = Date.now();
      try {
        const response = await mutateAsync({
          file: file,
          type: options.quizType,
          difficulty: quizLevelToApiQuizDifficulty(options.quizLevel),
          quizCount: options.quizCount,
          tryCount,
          locale: options.language,
          userPrompt,
          selectedStandardCodes: standards.map(standard => standard.code),
          generationId,
          generationMode,
          uiCohort,
        });
        // 생성된 퀴즈 아이템과 함께 상위 컴포넌트의 onGenerate 콜백 호출
        onGenerateSuccess(response.questions);
        const completedEventPayload = {
          ...options,
          ...getPromptData(
            {
              type: 'file' as const,
              data: file,
            },
            userPrompt || '',
          ),
          ...getStandardCodeData(standards),
          is_retry: tryCount > 0,
          file_type: file.type,
          file_size_mb: Math.round((file.size / 1024 ** 2) * 100) / 100,
          latency_ms: Date.now() - startTime,
          requested_question_count: options.quizCount,
          sample_question: response.questions[0].question,
          sample_answer: response.questions[0].answer,
          sample_options: response.questions[0].options,
          sample_type: response.questions[0].type,
          generation_id: response.generationId || generationId,
        };
        logger.track(AI_GENERATION_EVENT_KEY, completedEventPayload);
        logger.track('biz_ai_generation_completed', completedEventPayload);
      } catch (error) {
        onGenerateError();
        logger.track('biz_ai_generation_failed', {
          generation_id: generationId,
          error_type: error instanceof Error ? error.name : 'unknown',
          attempt_no: tryCount,
          latency_ms: Date.now() - startTime,
          source_type: 'file',
        });
        fileErrorAlert(
          error as Error,
          tryCount > 0
            ? 'ai_문제생성_파일_다시생성_에러'
            : 'ai_문제생성_파일_시작_에러',
        );
      }
      // BuildPromptOptions를 API 요청 형식으로 매핑
    },
    [
      mutateAsync,
      onGenerateSuccess,
      onGenerateStart,
      onGenerateError,
      tryCount,
      fileErrorAlert,
      generationId,
    ],
  );

  // 불필요한 리렌더링 방지를 위해 반환 객체 메모이제이션
  return useMemo(
    () => ({
      handleAttach,
      handleDetach,
      handleGenerate,
      isGenerating: isPending,
    }),
    [handleAttach, handleDetach, handleGenerate, isPending],
  );
};

/**
 * 파일 타입 검증
 * @param file - 검증할 파일 객체
 * @returns 파일 타입 유효 여부
 */
const validateFileType = (file: File): boolean => {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
  return ALLOWED_AI_FILE_EXTENSIONS.includes(fileExt);
};

/**
 * 파일 크기 검증
 * @param file - 검증할 파일 객체
 * @returns 파일 크기 유효 여부
 */
const validateFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};
